import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { getAuthCallbackLocation } from '../lib/authCallbackCapture'
import { trackSignupCompletedIfNewUser } from '../lib/signupAnalytics'
import { getPostAuthLandingPath } from '../lib/resumeOnlyMode'
import { supabase } from '../lib/supabase'

import { completeAuthCallback } from './authCallbackUtils'

async function resolvePostAuthLandingPath(userId: string): Promise<string> {
  const { data } = await supabase
    .from('user_onboarding')
    .select('completed_at')
    .eq('user_id', userId)
    .maybeSingle()

  return getPostAuthLandingPath({ onboardingComplete: !!data?.completed_at })
}

function AuthCallback() {
  const navigate = useNavigate()
  const [message, setMessage] = useState('Signing in...')
  const finishedRef = useRef(false)
  const callbackLocationRef = useRef(getAuthCallbackLocation())

  useEffect(() => {
    let cancelled = false

    const finish = async (
      target: 'home' | '/login',
      sessionUserId?: string,
      errorMessage?: string,
    ) => {
      if (finishedRef.current || cancelled) {
        return
      }
      finishedRef.current = true
      if (target === 'home') {
        const landingPath = sessionUserId
          ? await resolvePostAuthLandingPath(sessionUserId)
          : getPostAuthLandingPath({ onboardingComplete: false })
        navigate(landingPath, { replace: true })
        return
      }
      const query = errorMessage ? `?oauth_error=${encodeURIComponent(errorMessage)}` : ''
      navigate(`/login${query}`, { replace: true })
    }

    void (async () => {
      const result = await completeAuthCallback(callbackLocationRef.current)
      if (cancelled || finishedRef.current) {
        return
      }

      if (result.ok) {
        await trackSignupCompletedIfNewUser(result.userId)
        await finish('home', result.userId)
        return
      }

      setMessage('Finishing sign-in...')
      await new Promise((resolve) => setTimeout(resolve, 1500))
      if (cancelled || finishedRef.current) {
        return
      }

      const retry = await completeAuthCallback(callbackLocationRef.current)
      if (retry.ok) {
        await trackSignupCompletedIfNewUser(retry.userId)
        await finish('home', retry.userId)
        return
      }

      await finish('/login', undefined, retry.error)
    })()

    const timeout = setTimeout(() => {
      if (finishedRef.current || cancelled) {
        return
      }
      void completeAuthCallback(callbackLocationRef.current).then(async (result) => {
        if (finishedRef.current || cancelled) {
          return
        }
        if (result.ok) {
          await trackSignupCompletedIfNewUser(result.userId)
          await finish('home', result.userId)
        } else {
          await finish('/login', undefined, result.error || 'Sign-in timed out. Try again.')
        }
      })
    }, 30000)

    return () => {
      cancelled = true
      clearTimeout(timeout)
    }
  }, [navigate])

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-gray-600">{message}</div>
    </div>
  )
}

export default AuthCallback
